// Aura Glass Today view — Zen Focus hub + chronological timeline layout.
// F7 parity: renders the SAME feature blocks as classic (AttentionDigest,
// GettingStartedCard, InsightsCard, weekly/monthly review, monthly-close nudge)
// via useTodayOrchestration + the exported classic cards, styled by the
// .theme-aura overrides in bento-theme.css (semantic classes, F21).
import { useMemo } from "react";
import { AlarmClock, Calendar, CalendarCheck, Flame, Play, Plus, RotateCcw, Sparkles, Wallet } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { Task, Habit } from "../types";
import type { EntityKind } from "./entity-form";
import { setMonthlyCloseDismissed } from "../lib/app-prefs";
import { useTodayOrchestration } from "../hooks/use-today-orchestration";
import { moneyShort } from "./bento-ui";
import { aiAvailable } from "../lib/ai/ai-config";
import { AttentionDigest, GettingStartedCard, InsightsCard } from "./today-view";
import { FocusTimer } from "./focus-timer";
import { MorningPlanningModal } from "./morning-planning-modal";
import { EveningShutdownModal } from "./evening-shutdown-modal";
import { WeeklyReviewModal } from "./weekly-review-modal";
import { SetupGeneratorModal } from "./setup-generator-modal";
import { PromptDialog } from "./confirm-dialog";
import { REPLAN_REASONS } from "./replan-stats-derive";

interface Props {
  api: LifeOsApi;
  onGoto: (v: string) => void;
  onForm: (kind: EntityKind, item?: Record<string, unknown>) => void;
}

export function AuraTodayView({ api, onGoto, onForm }: Props) {
  const {
    today, d, focusTasks, habitsToday,
    plan, capacity, planned, overCapacity, capacityPercent,
    reviewDue, digest, insights,
    gsSteps, showGettingStarted, handleGsDismiss, handleGsStepClick,
    digestDismissed, dismissDigest,
    showMorning, setShowMorning,
    showEvening, setShowEvening,
    showReview, setShowReview,
    showSetup, setShowSetup,
    showReplanPrompt, setShowReplanPrompt, handleReplan, submitReplan,
    focusTask, handleStartFocus, handleFocusDone, handleFocusStop, handleFocusLogTime,
  } = useTodayOrchestration(api, onForm);

  // Greeting based on hour
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Chào buổi sáng" : hour < 18 ? "Chào buổi chiều" : "Chào buổi tối";

  // Build the unified Daily Stream (Timeline) items
  const timelineItems = useMemo(() => {
    const items: Array<{
      id: string;
      time?: string;
      type: "event" | "task" | "habit";
      title: string;
      done: boolean;
      priority?: "high" | "medium" | "low";
      meta?: string;
      rawItem: unknown;
    }> = [];

    // 1. Add Events
    d.todayEvents.forEach((ev: { id: string; time: string; title: string; color?: string }) => {
      items.push({
        id: ev.id,
        time: ev.time || "Cả ngày",
        type: "event",
        title: ev.title,
        done: false,
        meta: undefined,
        rawItem: ev,
      });
    });

    // 2. Add Focus Tasks (MITs) and other tasks due today
    const mitIds = new Set(focusTasks.map((t) => t.id));
    const allTodayTasks = api.state.tasks.filter(
      (t) => t.dueDate === today || t.status === "doing"
    );

    allTodayTasks.forEach((t: Task) => {
      const isMit = mitIds.has(t.id);
      const projName = t.projectId ? api.state.projects.find(p => p.id === t.projectId)?.name : undefined;
      items.push({
        id: t.id,
        time: isMit ? "★ Focus" : undefined,
        type: "task",
        title: t.title,
        done: t.status === "done",
        priority: t.priority,
        meta: `${t.estimateMinutes}m` + (projName ? ` · Dự án: ${projName}` : ""),
        rawItem: t,
      });
    });

    // 3. Add Habits
    api.state.habits.forEach((h: Habit) => {
      items.push({
        id: h.id,
        type: "habit",
        title: h.name,
        done: h.completions.includes(today),
        meta: `Mục tiêu: ${h.targetPerWeek} lần/tuần`,
        rawItem: h,
      });
    });

    // Sort: Pinned/MIT Tasks and events with times first, then regular tasks, then habits.
    return items.sort((a, b) => {
      // Prioritize time-based items
      if (a.time && !b.time) return -1;
      if (!a.time && b.time) return 1;
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }
      // Prio type: task > habit
      if (a.type !== b.type) {
        if (a.type === "task") return -1;
        if (b.type === "task") return 1;
      }
      return 0;
    });
  }, [d.todayEvents, focusTasks, api.state.tasks, api.state.habits, api.state.projects, today]);

  // SVG Circular progress configurations
  const radius = 55;
  const stroke = 8;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (capacityPercent / 100) * circumference;

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-10">
      {/* A3: Needs-attention digest — parity with classic; hidden during first-run. */}
      {!digestDismissed && !showGettingStarted && (
        <AttentionDigest
          digest={digest}
          onDismiss={dismissDigest}
          onGoto={onGoto}
          onOpenMorning={() => setShowMorning(true)}
          onOpenWeeklyReview={() => setShowReview("week")}
          onOpenInbox={() => onGoto("inbox")}
          onOpenMonthlyClose={() => {
            // Bấm chip = chủ động chốt → xoá cờ "Để sau" để Finance tự bật popup, rồi sang Tài chính.
            setMonthlyCloseDismissed("");
            onGoto("finance");
          }}
        />
      )}

      {/* AI starter kit — first-run shortcut to populate an empty account fast. */}
      {showGettingStarted && aiAvailable(api) && (
        <button
          onClick={() => setShowSetup(true)}
          className="gbrand flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-[13.5px] font-bold text-white shadow-sm transition hover:opacity-95"
        >
          <Sparkles className="h-4 w-4" />
          Để AI dựng bộ khởi đầu theo công việc của bạn
        </button>
      )}

      {/* A1: Getting-started checklist — parity with classic. */}
      {showGettingStarted && (
        <GettingStartedCard
          steps={gsSteps}
          onStepClick={handleGsStepClick}
          onDismiss={handleGsDismiss}
        />
      )}

      {/* 1. Welcoming Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-4">
        <div>
          <h3 className="text-[14px] font-bold text-cyan-500 uppercase tracking-widest flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-cyan-400 animate-pulse" /> {greeting}
          </h3>
          <h2 className="text-3xl font-display font-extrabold tracking-tight mt-1 text-ink-900">
            {api.auth.user ? api.auth.user.email?.split("@")[0] : "Trưởng nhóm"} Workspace
          </h2>
        </div>

        {/* Minimal stats ribbon */}
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-1.5 rounded-full bg-white/40 dark:bg-slate-900/40 border border-white/20 px-3.5 py-1.5 text-[12px] font-bold shadow-sm backdrop-blur-md">
            <AlarmClock className="h-3.5 w-3.5 text-rose-500" />
            <span className="text-rose-600 dark:text-rose-400">{d.overdue.length} Quá hạn</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-white/40 dark:bg-slate-900/40 border border-white/20 px-3.5 py-1.5 text-[12px] font-bold shadow-sm backdrop-blur-md">
            <Flame className="h-3.5 w-3.5 text-orange-500" />
            <span className="text-orange-600 dark:text-orange-400">{habitsToday}/{d.habitsTotal} Thói quen</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-white/40 dark:bg-slate-900/40 border border-white/20 px-3.5 py-1.5 text-[12px] font-bold shadow-sm backdrop-blur-md">
            <Wallet className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-emerald-600 dark:text-emerald-400">{moneyShort(d.spentToday)} Chi tiêu</span>
          </div>
        </div>
      </div>

      {/* Proactive insights — parity with classic; hidden during first-run. */}
      {!showGettingStarted && insights.length > 0 && (
        <InsightsCard insights={insights} onGoto={onGoto} />
      )}

      {/* 2. Zen Focus Hub */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
        {/* Left: SVG Progress Ring */}
        <div className="md:col-span-5 rounded-3xl bg-white/30 dark:bg-slate-900/20 border border-white/20 dark:border-white/5 backdrop-blur-xl p-6 flex flex-col items-center justify-center text-center shadow-lg relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/5 to-transparent pointer-events-none" />

          <div className="relative flex items-center justify-center">
            {/* SVG circle */}
            <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
              <circle
                stroke="rgba(255,255,255,0.08)"
                fill="transparent"
                strokeWidth={stroke}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
              />
              <circle
                stroke="url(#cyanPurpleGradient)"
                fill="transparent"
                strokeWidth={stroke}
                strokeDasharray={circumference + " " + circumference}
                style={{ strokeDashoffset }}
                strokeLinecap="round"
                r={normalizedRadius}
                cx={radius}
                cy={radius}
                className="transition-all duration-500 ease-out"
              />
              <defs>
                <linearGradient id="cyanPurpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00f0ff" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute flex flex-col items-center justify-center">
              <span className="text-2xl font-extrabold tracking-tighter gtext">{capacityPercent}%</span>
              <span className="text-[10px] uppercase font-bold text-ink-500">Sức chứa</span>
            </div>
          </div>

          <div className="mt-4 space-y-1">
            <p className="text-[13px] font-bold text-ink-700">
              {planned}′ đã lên lịch / {capacity}′ giới hạn
            </p>
            <p className="text-[11px] text-ink-500">
              {overCapacity ? "⚠️ Hãy điều chỉnh lại mục tiêu để tránh quá tải" : "Mức độ công việc hôm nay ở trạng thái lý tưởng"}
            </p>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => setShowMorning(true)}
              className="rounded-xl bg-white/60 dark:bg-white/10 px-4 py-2 text-[12px] font-bold hover:bg-white/80 dark:hover:bg-white/20 transition border border-white/20 shadow-sm"
            >
              {plan ? "Sửa Kế Hoạch" : "Lên Kế Hoạch"}
            </button>
            {plan?.locked && (
              <button
                onClick={handleReplan}
                className="rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 px-4 py-2 text-[12px] font-bold hover:bg-amber-500/20 transition shadow-sm"
              >
                <RotateCcw className="h-3 w-3 inline mr-1" /> Re-plan
              </button>
            )}
          </div>
        </div>

        {/* Right: Floating Focus Task Card */}
        <div className="md:col-span-7 rounded-3xl bg-white/30 dark:bg-slate-900/20 border border-white/20 dark:border-white/5 backdrop-blur-xl p-6 flex flex-col justify-between shadow-lg relative overflow-hidden group">
          <div className="absolute -right-20 -top-20 w-44 height-44 bg-cyan-500/10 rounded-full filter blur-3xl pointer-events-none group-hover:bg-cyan-500/15 transition-all duration-300" />

          <div>
            <div className="flex items-center justify-between">
              <span className="inline-block rounded-full bg-cyan-500/10 dark:bg-cyan-500/20 border border-cyan-500/20 px-3 py-1 text-[11px] font-bold text-cyan-600 dark:text-cyan-300 uppercase tracking-widest">
                🎯 Tiêu điểm hôm nay (MIT)
              </span>
              <button onClick={() => onGoto("tasks")} className="text-[12px] font-bold text-cyan-500 hover:underline">
                Xem tất cả việc
              </button>
            </div>

            <div className="mt-5 space-y-2.5">
              {focusTasks.length > 0 ? (
                focusTasks.slice(0, 1).map((task) => (
                  <div key={task.id} className="space-y-3">
                    <h3 className="text-2xl font-extrabold tracking-tight text-ink-900 leading-snug">
                      {task.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] text-ink-500">
                        Ước lượng: <b>{task.estimateMinutes} phút</b> · Hạn: <b>{task.dueDate}</b>
                      </span>
                      {task.priority === "high" && (
                        <span className="rounded-full bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 text-[10px] font-bold text-rose-500">
                          Khẩn cấp
                        </span>
                      )}
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={() => handleStartFocus(task)}
                        className="gbrand inline-flex items-center gap-2 rounded-2xl px-6 py-3 text-[14px] font-bold text-white shadow-lg shadow-cyan-500/20 hover:scale-105 active:scale-95 transition"
                      >
                        <Play className="h-4.5 w-4.5 fill-white" /> Bắt đầu Tập trung
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-6 text-center md:text-left">
                  <p className="text-ink-600 font-semibold text-[15px]">Bạn chưa thiết lập tiêu điểm tiêu biểu cho hôm nay.</p>
                  <p className="text-ink-400 text-[12.5px] mt-1">Bấm nút "Lên Kế Hoạch" để cấu hình nhiệm vụ trọng tâm.</p>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-white/10 dark:border-white/5 pt-4 mt-6 flex flex-wrap items-center justify-between gap-2">
            <span className="text-[12px] text-ink-500">Chu kỳ ngày</span>
            <div className="flex flex-wrap items-center gap-2">
              {/* D30 parity: weekly-review nudge glows when due; monthly review kế bên. */}
              <button
                onClick={() => setShowReview("week")}
                className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-1.5 text-[12.5px] font-bold transition shadow-sm ${
                  reviewDue
                    ? "gbrand text-white"
                    : "border border-white/20 dark:border-white/5 bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10"
                }`}
              >
                <CalendarCheck className="h-3.5 w-3.5" />
                Review tuần{reviewDue ? " (đến hạn)" : ""}
              </button>
              <button
                onClick={() => setShowReview("month")}
                className="rounded-xl border border-white/20 dark:border-white/5 bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10 px-4 py-1.5 text-[12.5px] font-bold transition shadow-sm"
              >
                Review tháng
              </button>
              <button
                onClick={() => setShowEvening(true)}
                className="rounded-xl border border-white/20 dark:border-white/5 bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10 px-4 py-1.5 text-[12.5px] font-bold transition shadow-sm"
              >
                Đóng ngày hôm nay
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 3. The Daily Chronological Stream */}
      <div className="rounded-3xl bg-white/20 dark:bg-slate-900/10 border border-white/15 dark:border-white/5 p-6 backdrop-blur-xl shadow-xl">
        <h3 className="text-lg font-display font-extrabold text-ink-900 mb-6 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-cyan-500" /> Hành trình trong ngày
        </h3>

        {timelineItems.length > 0 ? (
          <div className="relative border-l border-cyan-500/20 dark:border-cyan-500/10 pl-6 space-y-6 ml-3">
            {timelineItems.map((item) => {
              const itemDone = item.done;
              const typeIcon =
                item.type === "event" ? "📅" : item.type === "habit" ? "🔥" : "🎯";

              return (
                <div
                  key={item.id}
                  className={`relative group transition-all duration-300 ${
                    itemDone ? "opacity-50 line-through" : ""
                  }`}
                >
                  {/* Timeline bullet dot */}
                  <span className={`absolute -left-[31px] top-1.5 h-[11px] w-[11px] rounded-full border-2 border-white dark:border-slate-950 transition-all duration-300 ${
                    itemDone
                      ? "bg-emerald-500 shadow-[0_0_8px_#10b981]"
                      : item.time
                      ? "bg-cyan-500 shadow-[0_0_8px_#00f0ff]"
                      : "bg-ink-400"
                  }`} />

                  {/* Flow item glass panel */}
                  <div className="rounded-2xl border border-white/15 dark:border-white/5 bg-white/30 dark:bg-slate-900/15 p-4 hover:border-cyan-500/30 hover:bg-white/50 dark:hover:bg-slate-900/25 transition-all duration-300 shadow-sm flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-bold text-ink-500 uppercase tracking-wide">
                          {typeIcon} {item.time || "Nhiệm vụ"}
                        </span>
                        {item.priority === "high" && !itemDone && (
                          <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-ping" />
                        )}
                      </div>
                      <p className={`text-[15px] font-bold text-ink-900 mt-1 ${itemDone ? "text-ink-400" : ""}`}>
                        {item.title}
                      </p>
                      {item.meta && (
                        <p className="text-[12px] text-ink-500 mt-0.5">{item.meta}</p>
                      )}
                    </div>

                    <div className="shrink-0 flex items-center gap-2">
                      {item.type === "task" && !itemDone && (
                        <button
                          onClick={() => handleStartFocus(item.rawItem as Task)}
                          className="rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300 hover:bg-cyan-500/20 border border-cyan-500/10 px-3 py-1.5 text-[12px] font-bold transition"
                        >
                          ▶ Tập trung
                        </button>
                      )}
                      {item.type === "habit" && (
                        <button
                          onClick={() => {
                            const habit = item.rawItem as Habit;
                            const isComp = habit.completions.includes(today);
                            const next = isComp
                              ? habit.completions.filter((c) => c !== today)
                              : [...habit.completions, today];
                            api.actions.updateHabit(habit.id, { completions: next });
                          }}
                          className={`rounded-xl border px-3 py-1.5 text-[12px] font-bold transition ${
                            itemDone
                              ? "bg-emerald-500/15 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                              : "bg-white/50 dark:bg-white/5 border-white/20 hover:bg-white/80"
                          }`}
                        >
                          {itemDone ? "✓ Xong" : "Tập thói quen"}
                        </button>
                      )}
                      {item.type === "task" && (
                        <button
                          onClick={() => {
                            const task = item.rawItem as Task;
                            api.actions.updateTask(task.id, {
                              status: task.status === "done" ? "todo" : "done",
                            });
                          }}
                          className={`rounded-xl border px-3 py-1.5 text-[12px] font-bold transition ${
                            itemDone
                              ? "bg-emerald-500/15 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                              : "bg-white/50 dark:bg-white/5 border-white/20 hover:bg-white/80"
                          }`}
                        >
                          {itemDone ? "✓ Xong" : "Hoàn thành"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-12 border border-dashed border-white/10 dark:border-white/5 rounded-2xl bg-white/10 dark:bg-slate-900/5 text-center">
            <p className="font-semibold text-ink-600">Hành trình trống</p>
            <p className="text-[12.5px] text-ink-400 mt-1 max-w-sm mx-auto">
              Chưa có lịch trình, thói quen hay công việc nào trong ngày hôm nay. Hãy sử dụng thanh công cụ bên dưới để tạo mới!
            </p>
          </div>
        )}
      </div>

      {/* 4. Glass floating quick capture */}
      <div className="rounded-2xl border border-white/20 dark:border-white/5 bg-white/40 dark:bg-slate-900/20 p-4 shadow-lg backdrop-blur-xl flex items-center gap-3">
        <input
          type="text"
          placeholder="Ghi nhanh việc hôm nay... (vd: Họp dự án lúc 10h)"
          className="flex-1 bg-white/20 dark:bg-slate-950/20 border-white/10 text-[14px]"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const val = e.currentTarget.value.trim();
              if (val) {
                api.actions.addTask({ title: val, dueDate: today, status: "todo", priority: "medium", estimateMinutes: 25, subtasks: [] });
                e.currentTarget.value = "";
              }
            }
          }}
        />
        <button
          onClick={() => onForm("task")}
          className="gbrand shrink-0 rounded-xl px-4 py-2.5 text-[13px] font-bold text-white shadow-md inline-flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> Thêm việc
        </button>
      </div>

      {/* Modals */}
      {showReplanPrompt && (
        <PromptDialog
          title="Lập lại kế hoạch hôm nay"
          message="Kế hoạch đã khoá — chọn lý do (giúp thống kê), hoặc mở lại luôn."
          placeholder="Lý do khác… (tuỳ chọn)"
          submitLabel="Mở khoá & lập lại"
          allowEmpty
          presets={REPLAN_REASONS}
          onSubmit={submitReplan}
          onClose={() => setShowReplanPrompt(false)}
        />
      )}

      {showSetup && <SetupGeneratorModal api={api} onClose={() => setShowSetup(false)} />}
      {showMorning && <MorningPlanningModal api={api} onClose={() => setShowMorning(false)} />}
      {showEvening && <EveningShutdownModal api={api} onClose={() => setShowEvening(false)} />}
      {showReview && <WeeklyReviewModal api={api} scope={showReview} onClose={() => setShowReview(null)} />}

      {/* D35: Focus timer floating card. key — switching tasks must remount the
          timer (its session lives in a useState initializer bound to the task). */}
      {focusTask && (
        <FocusTimer
          key={focusTask.id}
          task={focusTask}
          onDone={handleFocusDone}
          onStop={handleFocusStop}
          onLogTime={handleFocusLogTime}
        />
      )}
    </div>
  );
}
